import { Fragment } from 'react';
import { STYLE_SIZES, STYLE_KINDS, companyRows } from '../../store/portfolioAnalysis.js';
import { money, compact } from './format.js';

// The optional third picture, when statements have been supplied and accepted.
//
// Deliberately not a narrative. Two tables, a grid and a short scope note, on
// the instruction that this page is analysis rather than an essay. Every
// percentage names the total it is a share of, because the two denominators
// here are easy to mistake for each other and for the household's wealth, which
// is neither of them.

const UNAVAILABLE = {
  absent: {
    title: 'No statements have been supplied',
    body: 'Statements would add what the accounts actually hold, and can be added before or after '
      + 'this meeting.',
  },
  pending: {
    title: 'The statements are still being read',
    body: 'This takes about a minute. The meeting does not wait for it, and the results can be '
      + 'reviewed before the next conversation.',
  },
  failed: {
    title: 'The statements could not be read',
    body: 'Nothing from them is shown, because a partial read is not a portfolio. The rest of the '
      + 'assessment is unaffected.',
  },
  unreviewed: {
    title: 'The results have not been reviewed yet',
    body: 'An analysis is not shown to anyone until an advisor has checked it against the '
      + 'statements it came from.',
  },
};

function pct(n, digits = 1) {
  return n === null || n === undefined ? '' : `${n.toFixed(digits)}%`;
}

// Parsed from its parts rather than handed to `new Date`, which reads a bare
// YYYY-MM-DD as UTC midnight and then prints it in local time, moving a
// statement dated the 31st to the 30th for anyone west of Greenwich. The
// selectors already avoid this the same way.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function fmtDate(iso) {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (m) {
    const month = MONTHS[Number(m[2]) - 1];
    return month ? `${month} ${Number(m[3])}, ${m[1]}` : String(iso);
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function Companies({ exposure, limit = 0, title = 'What you own', brief = false }) {
  if (!exposure) return null;
  const { rows, rest } = companyRows(exposure, limit);
  return (
    <section className="conv-an-block">
      <h3>{title}</h3>
      <table className="conv-an-table">
        <thead>
          <tr>
            <th scope="col">Company</th>
            <th scope="col">Held directly</th>
            <th scope="col">Through funds</th>
            <th scope="col">Combined</th>
            <th scope="col">Share of analyzed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.name}>
              <th scope="row">
                {r.name}
                {r.viaCount > 1 ? <small>{` in ${r.viaCount} holdings`}</small> : null}
              </th>
              <td>{r.directValue > 0 ? money(r.directValue) : '—'}</td>
              <td>{r.fundValue > 0 ? money(r.fundValue) : '—'}</td>
              <td>{money(r.combined)}</td>
              <td>{pct(r.pctOfPriced)}</td>
            </tr>
          ))}
          {rest > 0 && (
            <tr className="conv-an-rest">
              <th scope="row">All other identified companies</th>
              <td>{'—'}</td>
              <td>{'—'}</td>
              <td>{money(rest)}</td>
              <td>{pct((rest / exposure.pricedValue) * 100)}</td>
            </tr>
          )}
          <tr className="conv-an-rest">
            <th scope="row">Not attributed to a company</th>
            <td>{'—'}</td>
            <td>{'—'}</td>
            <td>{money(exposure.unmappedValue)}</td>
            <td>{exposure.pricedValue>0?pct((exposure.unmappedValue / exposure.pricedValue) * 100):''}</td>
          </tr>
        </tbody>
      </table>
      <p className="conv-caption">
        {brief ? `Shares are of ${money(exposure.pricedValue)}. ` : `Shares are of ${money(exposure.pricedValue)}, the value the analysis could price. `}
        {exposure.isFloor
          ? 'Some funds could not be looked through, so each figure is at least this much rather than exactly this much. '
          : ''}
        {!brief && exposure.fundsUnavailable.length
          ? `No holdings list was available for ${exposure.fundsUnavailable.join(', ')}. `
          : ''}
        Value that is not attributed to a company is unidentified, not absent.
      </p>
    </section>
  );
}

export function StyleGrid({ style, brief = false }) {
  if (!style) return null;
  if (!style.grid) {
    return (
      <section className="conv-an-block">
        <h3>Equity by size and style</h3>
        <p className="conv-quiet">
          {`None of the ${money(style.equityValue)} of equity carries a published size and style, so there is no grid to draw.`}
        </p>
      </section>
    );
  }
  const max = Math.max(...style.grid.flat().map(c => c.value), 1);
  return (
    <section className="conv-an-block">
      <h3>Equity by size and style</h3>
      <div className="conv-style">
        <div className="conv-style-corner" />
        {STYLE_KINDS.map(k => <div className="conv-style-head" key={k}>{k}</div>)}
        {STYLE_SIZES.map((size, i) => (
          <Fragment key={size}>
            <div className="conv-style-side">{size}</div>
            {style.grid[i].map(cell => (
              <div
                className="conv-style-cell"
                key={`${cell.size}-${cell.style}`}
                style={{ '--fill': `${(cell.value / max) * 100}%` }}
              >
                <strong>{cell.value > 0 ? compact(cell.value) : '—'}</strong>
                <span>{cell.value > 0 ? pct(cell.pct, 0) : ''}</span>
              </div>
            ))}
          </Fragment>
        ))}
      </div>
      <p className="conv-caption">
        {`Percentages are of ${money(style.classifiedValue)}, the equity whose style is published. `}
        {style.unknownEquityValue > 0
          ? `A further ${money(style.unknownEquityValue)} of equity has no published style (${style.unknown.map(u => u.label).join(', ')}) and is not in the grid. `
          : ''}
        {style.nonEquityValue > 0
          ? `${money(style.nonEquityValue)} is not equity and is excluded entirely. `
          : ''}
        {brief ? '' : 'A fund is placed by its published classification, never by its name.'}
      </p>
    </section>
  );
}

function Context({ rows }) {
  if (!rows?.length) return null;
  return (
    <section className="conv-an-block">
      <h3>Two things worth looking at together</h3>
      {rows.map(r => (
        <div className="conv-an-pair" key={r.key}>
          <div>
            <span>{r.left.label}</span>
            <strong>{r.money ? money(r.left.value) : r.left.value}</strong>
          </div>
          <div>
            <span>{r.right.label}</span>
            <strong>{r.money ? money(r.right.value) : r.right.value}</strong>
          </div>
          <p>{r.question}</p>
        </div>
      ))}
      <p className="conv-caption">
        These place two recorded figures side by side. They are not conclusions about risk,
        adequacy or whether a retirement is affordable.
      </p>
    </section>
  );
}

export function Scope({ scope }) {
  if (!scope) return null;
  const dates = [...new Set(scope.accounts.map(a => a.asOf).filter(Boolean))];
  return (
    <section className="conv-an-block conv-an-scope">
      <h3>What this covers, and when it was true</h3>
      <ul>
        <li>{`${money(scope.analyzedValue)} across ${scope.accounts.length} supplied ${scope.accounts.length === 1 ? 'account' : 'accounts'}.`}</li>
        {dates.length > 1 ? (
          <li>{`The statements are valued on different days: ${dates.map(fmtDate).join(' and ')}.`}</li>
        ) : dates.length === 1 ? (
          <li>{`Statement values as of ${fmtDate(dates[0])}.`}</li>
        ) : null}
        {scope.constituentsAsOf ? (
          <li>{`Fund holdings as published on ${fmtDate(scope.constituentsAsOf)}, which is not the same as today's exposure.`}</li>
        ) : (
          <li>Fund holdings are as last published by each fund, which is not the same as today&apos;s exposure.</li>
        )}
        {scope.unsupportedValue > 0 && (
          <li>{`${money(scope.unsupportedValue)} could not be priced and is excluded from every figure above: ${scope.unsupported.map(u => u.name).join(', ')}.`}</li>
        )}
        <li>Only the accounts whose statements were supplied are here. Anything not sent is not represented.</li>
      </ul>
    </section>
  );
}

export default function PortfolioAnalysisView({ availability, exposure, style, context, scope, simulated }) {
  if (availability.state !== 'ready') {
    const copy = UNAVAILABLE[availability.state] || UNAVAILABLE.absent;
    return (
      <div className="conv-empty">
        <div className="conv-eyebrow">Portfolio analysis</div>
        <h2>{copy.title}</h2>
        <p>{copy.body}</p>
        {availability.reason ? <p className="conv-caption">{availability.reason}</p> : null}
      </div>
    );
  }

  return (
    <>
      {simulated && (
        <p className="conv-simulated">
          Simulated holdings for demonstration. These are fictional companies and values, not a
          client portfolio.
        </p>
      )}
      <h2>Your current portfolio</h2>
      <div className="conv-analysis-grid"><Companies exposure={exposure} /><StyleGrid style={style} /></div>
      <Context rows={context} />
      <Scope scope={scope} />
    </>
  );
}
